import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@authentication/auth.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from '@shell/message.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AeroplantasService } from '@management/asignacion-aeroplantas/services/aeroplantas.service';
import { Rol } from '@shared/models/notificaciones/Rol';
import { isEmptyValue } from '@shared/compat-tools';
import { Asignacion } from '@shared/models/usuarios/Asignacion';
import { TipoUsuario } from '@shared/enums/tipoUsuario';

@Component({
  selector: 'app-asignar-aeroplantas-detalle',
  templateUrl: './asignar-aeroplantas-detalle.component.html',
  styleUrl: './asignar-aeroplantas-detalle.component.scss'
})
export class AsignarAeroplantasDetalleComponent implements OnChanges, OnInit {

  @Input() asignacionSeleccionada: Asignacion;

  loading: boolean;
  guardandoDatos: boolean;
  cargandoUsuarios: boolean;
  cargandoRoles: boolean;

  todosLosRolesSolicitados: Rol[] = [];
  todosLosRolesAprobadores: Rol[] = [];

  rolesSolicitados: Rol[] = [];
  rolesAprobadores: Rol[] = [];

  TipoUsuario = TipoUsuario;

  dataForm = new FormGroup({
    nombreFormControl: new FormControl(null, Validators.required),
    rolSolicitanteFormControl: new FormControl(null),
    rolAprobadorFormControl: new FormControl(null)
  });

  constructor(
    private spinner: NgxSpinnerService,
    public activeModal: NgbActiveModal,
    public authService: AuthService,
    private messageService: MessageService,
    private aeroplantasService: AeroplantasService) {}

  ngOnInit(): void {
    this.recargarRoles();
  }

  ngOnChanges() {
    if(this.estaEditandoAsignacion()){
      this.cargarFormulario()
    }
  }

  cargarFormulario() {
    this.nombreFormControl.setValue(this.asignacionSeleccionada.nombreAsignacion);
    this.rolesSolicitados = this.asignacionSeleccionada.rolesSolicitantes;
    this.rolesAprobadores = this.asignacionSeleccionada.rolesAprobadores;
    this.recargarRoles();
  }

  recargarRoles() {
    this.todosLosRolesSolicitados = this.aeroplantasService.listaRoles().filter( r => !this.rolesSolicitados.some((rol: Rol) => rol.id === r.id) );
    this.todosLosRolesAprobadores = this.aeroplantasService.listaRoles().filter( r => !this.rolesAprobadores.some((rol: Rol) => rol.id === r.id) );    
  }

  agregaRolSolicitante() {
    this.commonAddRols(this.rolesSolicitados, this.rolSolicitanteFormControl, false);  
  }

  agregaRolAprobador() {
    this.commonAddRols(this.rolesAprobadores, this.rolAprobadorFormControl, true);
  }

  commonAddRols(listaRoles: Rol[], control: AbstractControl, esAprobador: boolean) {
    if (isEmptyValue(control.value)) {
      this.messageService.showErrorMessage(`Debe seleccionar un rol ${esAprobador ? 'aprobador' : 'solicitante'} a asignar.`);
      return; 
    }

    if(listaRoles.some( r => r.id == control.value.id )) {
      this.messageService.showErrorMessage(`El rol ${ esAprobador ? 'aprobador' : 'solicitante' } seleccionado ya se encuentra en la lista.`);
      return;
    }

    listaRoles.push(control.value);
    control.reset();

    this.recargarRoles();
  }

  quitarRolSolicitante(rol) {
    this.rolesSolicitados = this.rolesSolicitados.filter( r => r.id != rol.id );
    this.recargarRoles();
  }

  quitarRolAprobador(rol) {
    this.rolesAprobadores = this.rolesAprobadores.filter( r => r.id != rol.id );
    this.recargarRoles();
  }

  resetForm() {
    this.dataForm.reset();
  }

  estaEditandoAsignacion() {
    if (this.asignacionSeleccionada) {
      return true;
    } else {
      return false;
    }
  }

  handleSpinner(load: boolean) {
    load ? this.spinner.show('spinnerGrafico') : this.spinner.hide('spinnerGrafico');
    this.loading = load;
  }

  guardarCambios() {
    this.accionarForm();

    if(this.formularioValido()){
      this.guardandoDatos = true;
      this.handleSpinner(true);

      const nuevo = new Asignacion();
      nuevo.nombreAsignacion = this.nombreFormControl.value;
      nuevo.rolesSolicitantes = this.rolesSolicitados;
      nuevo.rolesAprobadores = this.rolesAprobadores;
      
      if(this.estaEditandoAsignacion()){
        nuevo.id = this.asignacionSeleccionada.id;
        this.aeroplantasService.putAsignaciones(nuevo)
          .subscribe(r => {
            console.log(r)
            this.guardandoDatos = false;
            this.aeroplantasService.recargarAsignaciones.next(true); // cuando la llamada retorne un 200 OK
            this.handleSpinner(false);
          }, () => {
            this.guardandoDatos = false;            
            this.handleSpinner(false);
          });        
      } else {
        this.aeroplantasService.postAsignaciones(nuevo)
          .subscribe(r => {
            console.log(r)
            this.guardandoDatos = false;
            this.aeroplantasService.recargarAsignaciones.next(true); // cuando la llamada retorne un 200 OK
            this.activeModal.close();
            this.handleSpinner(false);
          }, () => {
            this.guardandoDatos = false;            
            this.handleSpinner(false);
          });
      }
    }
  }

  formularioValido(){
    let error: string = '';

    if(this.dataForm.invalid || isEmptyValue(this.rolesAprobadores) || isEmptyValue(this.rolesSolicitados)){
      if (this.nombreFormControl.invalid) {
        error = 'Debe seleccionar un nombre de asignación válido.'
      }
      if (isEmptyValue(this.rolesAprobadores)) {
        error = 'Debe seleccionar al menos un rol aprobador.'
      }
      if (isEmptyValue(this.rolesSolicitados)) {
        error = 'Debe seleccionar al menos un rol solicitante.'
      }      
    }

    if (error != '') {
      this.messageService.showErrorMessage(error);
      return false;
    }

    return true;
  }

  accionarForm() {
    Object.keys(this.dataForm.controls).forEach(key => {
      this.dataForm.get(key).markAsDirty();
    });
  }

  /** getters */

  get nombreFormControl() {
    return this.dataForm.get('nombreFormControl');
  }

  get rolSolicitanteFormControl() {
    return this.dataForm.get('rolSolicitanteFormControl');
  }

  get rolAprobadorFormControl() {
    return this.dataForm.get('rolAprobadorFormControl');
  }  
}
